import React, { useState, useEffect } from 'react';
import {
  Routes,
  Route,
  useNavigate,
  useParams,
  useLocation as useRouterLocation,
} from 'react-router-dom';
import { Plus, AlertTriangle } from 'lucide-react';
import { Toaster } from 'react-hot-toast';
import { LocationCard } from './components/LocationCard';
import { Filters } from './components/Filters';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { AddLocation } from './components/AddLocation';
import { EmptyState } from './components/EmptyState';
import { Pagination } from './components/Pagination';
import { Location, FilterState, UserLocation, ViewMode } from './types';
import { getLocations, addLocation } from './utils/api';
import { Map } from './components/Map';
import { LocationDetails } from './components/LocationDetails';
import { AdminPanel } from './admin/AdminPanel';
import { LocationProvider, useLocation } from './context/LocationContext';
import { AuthProvider } from './context/AuthContext';

const LOCATIONS_PER_PAGE = 21;

function MainPage() {
  const { userLocation, setUserLocation } = useLocation();
  const [locations, setLocations] = useState<Location[]>([]);
  const [totalLocations, setTotalLocations] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<FilterState>({
    category: 'all',
    searchQuery: '',
    radius: 150,
    tags: [],
    sortBy: 'distance'
  });

  const navigate = useNavigate();
  const { pathname } = useRouterLocation();

  useEffect(() => {
    loadLocations();

    if (navigator.geolocation) {
      const geolocationOptions = {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000
      };

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          try {
            const response = await fetch(
              `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=be`
            );
            const data = await response.json();
            setUserLocation({
              lat: latitude,
              lng: longitude,
              city: data.city || 'Невядомы горад',
            });
          } catch (error) {
            console.error('Памылка атрымання дадзеных пра месцазнаходжанне:', error);
            setUserLocation({
              lat: 53.9,
              lng: 27.5667,
              city: 'Мінск',
            });
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          let errorMessage = 'Не атрымалася вызначыць ваша месцазнаходжанне';
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Доступ да геалакацыі адхілены. Калі ласка, дазвольце доступ да месцазнаходжання ў наладах браўзера.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Інфармацыя пра месцазнаходжанне недаступная. Праверце, ці ўключаны сэрвісы геалакацыі на вашай прыладзе.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Час чакання адказу пра месцазнаходжанне скончыўся. Паспрабуйце абнавіць старонку.';
              break;
          }
          
          setUserLocation({
            lat: 53.9,
            lng: 27.5667,
            city: 'Мінск',
          });
        },
        geolocationOptions
      );
    } else {
      setUserLocation({
        lat: 53.9,
        lng: 27.5667,
        city: 'Мінск',
      });
    }
  }, []);

  const loadLocations = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { locations: data, total } = await getLocations(false, currentPage);
      setLocations(data);
      setTotalLocations(total);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Не атрымалася загрузіць лакацыі';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadLocations();
  }, [currentPage]);

  const handleAddLocation = async (newLocation: Omit<Location, 'id'>) => {
    try {
      const { data, error } = await addLocation(newLocation);
      if (error) {
        throw new Error(error);
      }
      if (data) {
        setShowAddLocation(false);
        setError(null);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Не атрымалася дадаць лакацыю';
      setError(errorMessage);
      throw err;
    }
  };

  const calculateDistance = (
    userLoc: UserLocation,
    locationCoords: { lat: number; lng: number }
  ): number => {
    if (!window.ymaps?.coordSystem?.geo) {
      return 0;
    }
    return (
      window.ymaps.coordSystem.geo.getDistance(
        [userLoc.lat, userLoc.lng],
        [locationCoords.lat, locationCoords.lng]
      ) / 1000
    );
  };

  const filteredAndSortedLocations = locations.filter((location) => {
    if (filters.category !== 'all' && location.category !== filters.category) {
      return false;
    }
    if (userLocation && filters.radius !== 800) {
      const distance = calculateDistance(userLocation, location.coordinates);
      if (distance > filters.radius) {
        return false;
      }
    }
    if (
      filters.tags.length > 0 &&
      !filters.tags.some((tag) => location.tags.includes(tag))
    ) {
      return false;
    }
    return true;
  }).sort((a, b) => {
    if (filters.sortBy === 'newest') {
      return new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime();
    } else {
      if (!userLocation) return 0;
      const distanceA = calculateDistance(userLocation, a.coordinates);
      const distanceB = calculateDistance(userLocation, b.coordinates);
      return distanceA - distanceB;
    }
  });

  const totalPages = Math.ceil(totalLocations / LOCATIONS_PER_PAGE);

  const handleLocationSelect = (location: Location) => {
    setSelectedLocation(location);
    navigate(`/l/${location.short_id}`);
  };

  const handleLocationClose = () => {
    setSelectedLocation(null);
    navigate('/');
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Toaster position="top-center" />
      <Header userLocation={userLocation} />

      <main className="container mx-auto px-4 py-8 flex-grow">
        {window.dbConnectionError && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-2 text-yellow-800">
              <AlertTriangle size={20} />
              <p className="font-medium">
                Увага! Праблемы з падключэннем да базы дадзеных.
              </p>
            </div>
            <p className="mt-1 text-yellow-700">
              Паказваюцца тэставыя дадзеныя. Некаторыя функцыі могуць быць
              недаступныя.
            </p>
          </div>
        )}

        <div className="flex flex-col gap-4 mb-8">
          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
            <div className="max-w-2xl">
              <p className="text-base md:text-lg text-gray-700 leading-relaxed">
                <span className="hidden md:inline">
                  Ласкава запрашаем у "Мой Куток" - месца, дзе вы можаце знайсці
                  і падзяліцца цікавымі прыроднымі лакацыямі Беларусі. Тут
                  збіраюцца не толькі экасцежкі, месцы для кемпінгу, пікніка і
                  назірання за зоркамі, але і проста прыгожыя куткі прыроды для
                  шпацыраў і адпачынку. Далучайцеся да нашай супольнасці,
                  дадавайце свае любімыя месцы і адкрывайце новыя куткі роднай
                  прыроды!
                </span>
                <span className="md:hidden">
                  Знаходзьце цікавыя прыродныя лакацыі Беларусі: экасцежкі,
                  месцы для кемпінгу, пікніка і назірання за зоркамі. Дадавайце
                  свае любімыя месцы і адкрывайце новыя куткі роднай прыроды!
                </span>
              </p>
            </div>
            <button
              onClick={() => setShowAddLocation(true)}
              className="flex items-center justify-center gap-2 px-6 py-2 bg-primary text-white rounded-full hover:bg-primary/90 transition-colors w-full md:w-auto"
            >
              <Plus size={20} />
              Дадаць лакацыю
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <p className="font-medium">Памылка:</p>
            <p>{error}</p>
          </div>
        )}

        <Filters
          filters={filters}
          viewMode={viewMode}
          onFilterChange={setFilters}
          onViewModeChange={setViewMode}
        />

        {viewMode === 'list' ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {isLoading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <LocationCard
                    key={index}
                    location={{} as Location}
                    isLoading={true}
                    onSelect={() => {}}
                  />
                ))
              ) : filteredAndSortedLocations.length === 0 ? (
                <div className="col-span-full">
                  <EmptyState />
                </div>
              ) : (
                filteredAndSortedLocations.map((location) => (
                  <LocationCard
                    key={location.id}
                    location={location}
                    userLocation={userLocation}
                    onSelect={handleLocationSelect}
                  />
                ))
              )}
            </div>
            {!isLoading && filteredAndSortedLocations.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </>
        ) : (
          <div className="h-[calc(100vh-16rem)] rounded-xl overflow-hidden">
            <Map
              center={
                userLocation
                  ? [userLocation.lat, userLocation.lng]
                  : [53.9, 27.5667]
              }
              locations={filteredAndSortedLocations}
              onLocationClick={handleLocationSelect}
              showUserLocation={Boolean(userLocation)}
            />
          </div>
        )}
      </main>

      <Footer />

      {showAddLocation && (
        <AddLocation
          onAdd={handleAddLocation}
          onClose={() => setShowAddLocation(false)}
        />
      )}

      {selectedLocation && (
        <LocationDetails
          location={selectedLocation}
          onClose={handleLocationClose}
          userLocation={userLocation}
        />
      )}
    </div>
  );
}

function LocationRoute() {
  const { shortId } = useParams();
  const navigate = useNavigate();
  const [location, setLocation] = useState<Location | null>(null);
  const { userLocation } = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadLocation = async () => {
      try {
        setIsLoading(true);
        const { locations } = await getLocations();
        const foundLocation = locations.find((loc) => loc.short_id === shortId);
        if (foundLocation) {
          setLocation(foundLocation);
        } else {
          setError('Лакацыя не знойдзена');
          navigate('/');
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Не атрымалася загрузіць лакацыю'
        );
        navigate('/');
      } finally {
        setIsLoading(false);
      }
    };

    loadLocation();
  }, [shortId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse">
          <div className="h-32 w-32 bg-gray-200 rounded-full"></div>
        </div>
      </div>
    );
  }

  if (!location) {
    return null;
  }

  return (
    <LocationDetails
      location={location}
      onClose={() => navigate('/')}
      userLocation={userLocation}
    />
  );
}

function App() {
  return (
    <AuthProvider>
      <LocationProvider>
        <Routes>
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/l/:shortId" element={<LocationRoute />} />
          <Route path="/" element={<MainPage />} />
        </Routes>
      </LocationProvider>
    </AuthProvider>
  );
}

export default App;